import { useDrinkHistory } from '@/features/drink-history/api/use-drink-history'
import { useNavigate } from '@tanstack/react-router'
import { useTelegram } from '@/shared/lib/telegram'
import { Skeleton } from '@/shared/ui'
import { cn } from '@/shared/lib/utils'

/**
 * Мини-виджет активности за неделю
 * 
 * Компактный график активности с кликабельным переходом в дневник.
 */
export function ProfileTeaStats() {
  const { data, isLoading } = useDrinkHistory({ limit: 50 })
  const navigate = useNavigate()
  const { hapticFeedback } = useTelegram()

  const entries = data?.data || []

  // Получаем активность за последние 7 дней
  const last7Days = Array.from({ length: 7 }, (_, i) => {
    const date = new Date()
    date.setDate(date.getDate() - (6 - i))
    return date.toISOString().split('T')[0]
  })

  const activityByDay = new Map<string, number>()
  entries.forEach((entry) => {
    const date = new Date(entry.drankAt).toISOString().split('T')[0]
    activityByDay.set(date, (activityByDay.get(date) || 0) + 1)
  })

  const weekDays = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс']

  const handleClick = () => {
    hapticFeedback.impactOccurred('light')
    navigate({ to: '/tea-diary' })
  }

  if (isLoading) {
    return (
      <div className="bg-white dark:bg-dark-card rounded-2xl p-4 shadow-sm">
        <Skeleton className="h-4 w-24 mb-3" />
        <div className="flex gap-1">
          {[1, 2, 3, 4, 5, 6, 7].map((i) => (
            <Skeleton key={i} className="flex-1 h-8 rounded" />
          ))}
        </div>
      </div>
    )
  }

  return (
    <button
      onClick={handleClick}
      className="w-full bg-white dark:bg-dark-card rounded-2xl p-4 shadow-sm text-left transition-all hover:shadow-md active:scale-[0.98] animate-in fade-in slide-in-from-bottom-2 duration-300 delay-125"
    >
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
        Активность за неделю
      </p>
      
      <div className="flex gap-1">
        {last7Days.map((day, index) => {
          const count = activityByDay.get(day) || 0
          const dayOfWeek = new Date(day).getDay()
          // Корректируем для русской локали (Пн = 0)
          const dayIndex = dayOfWeek === 0 ? 6 : dayOfWeek - 1
          
          return (
            <div key={day} className="flex-1 flex flex-col items-center gap-1">
              <div
                className={cn(
                  'w-full aspect-square rounded-md transition-colors',
                  count === 0 && 'bg-gray-100 dark:bg-dark-elevated',
                  count === 1 && 'bg-primary/30 dark:bg-primary/40',
                  count === 2 && 'bg-primary/60 dark:bg-primary/70',
                  count >= 3 && 'bg-primary dark:bg-primary'
                )}
              />
              <span className="text-[9px] text-gray-400 dark:text-gray-500">
                {weekDays[dayIndex]}
              </span>
            </div>
          )
        })}
      </div>
    </button>
  )
}
